import { useEntityFilter } from '@risksmart-app/components/src/contexts/entityFilterContext';
import type { ActionRegisterResponse } from '@risksmart-app/trpc/src/types';
import type {
  Action_Bool_Exp,
  GetActionsQuery,
} from '@risksmart-app/web-graphql-client/generated/graphql';
import {
  GetActionsDocument,
  Parent_Type_Enum,
} from '@risksmart-app/web-graphql-client/generated/graphql';
import { createQueryHook, type StrictMappedGraphQLSelection } from 'src/utils';

import useEntityWhereFilter from '@/hooks/useEntityWhereFilter';

export const mapTrpcActionsToGraphQL = (
  trpcData: ActionRegisterResponse
): StrictMappedGraphQLSelection<GetActionsQuery> => ({
  action: trpcData.action,
});

const useActionsGraphqlVariables = () => {
  const whereFilter = useEntityWhereFilter<Action_Bool_Exp>(
    Parent_Type_Enum.Action
  );

  return { where: whereFilter };
};

type InternalArgs = {
  parentId?: string;
  tagTypeIds?: string[];
  departmentTypeIds?: string[];
  entityIds?: string[];
};

const useGetActionsRegisterBase = createQueryHook<
  InternalArgs,
  ActionRegisterResponse,
  GetActionsQuery
>({
  trpcQueryOptions: (trpc, args) =>
    trpc.frontend.action.register.queryOptions({
      parentId: args.parentId,
      tagTypeIds: args.tagTypeIds,
      departmentTypeIds: args.departmentTypeIds,
      entityIds: args.entityIds,
    }),
  mapTrpcDataToGraphQL: mapTrpcActionsToGraphQL,
  graphqlDocument: GetActionsDocument,
  graphqlVariables: useActionsGraphqlVariables,
});

export type UseGetActionsRegisterArgs = {
  parentId?: string;
  tagTypeIds?: string[];
  departmentTypeIds?: string[];
};

/**
 * Fetches actions with automatic entity filtering for both GraphQL and tRPC
 * paths. Entity filtering uses `useEntityFilter` context internally — consumers
 * don't need to pass entityIds. Leaves the GraphQL path unchanged (the flag-off
 * behaviour flows through `useEntityWhereFilter`).
 */
export const useGetActionsRegister = (params: {
  queryArgs: UseGetActionsRegisterArgs;
  shouldSkip?: boolean;
}) => {
  const { entityIds } = useEntityFilter();

  return useGetActionsRegisterBase({
    queryArgs: {
      ...params.queryArgs,
      entityIds: entityIds?.length ? entityIds : undefined,
    },
    shouldSkip: params.shouldSkip,
  });
};
